import React, { FC, useState } from "react";
import styled from "styled-components";
import { MiqFieldUpdate, MiqListingUpdates } from "../../../utils/miq/miq-sync-mapper";
import { MiqUpdateSection, Checkbox } from "./miq-sync-update-section";

interface Props {
  listingUpdates: MiqListingUpdates;
  onApply: (selectedFields: MiqFieldUpdate[]) => void;
  onIgnore: () => void;
  isApplying?: boolean;
  isIgnoring?: boolean;
}

const MiqSyncReview: FC<Props> = ({
  listingUpdates,
  onApply,
  onIgnore,
  isApplying,
  isIgnoring,
}) => {
  const allFields = listingUpdates.updates.map((update) => update.field);

  // All fields, including previously discarded ones, are selected by default so re-reviewing a
  // discarded change still applies it unless the user explicitly unchecks it.
  const [selectedFields, setSelectedFields] = useState<string[]>(allFields);
  const allSelected = selectedFields.length === allFields.length;

  const toggleField = (field: string) => {
    setSelectedFields(
      selectedFields.includes(field)
        ? selectedFields.filter((selectedField) => selectedField !== field)
        : selectedFields.concat(field),
    );
  };

  return (
    <Overlay role="dialog" aria-modal="true" aria-labelledby="miq-sync-title">
      <ReviewPanel>
        <ReviewContent>
          <Header>
            <Title id="miq-sync-title">Review Updates From MIQ</Title>
            <Actions>
              <IgnoreButton type="button" onClick={onIgnore} disabled={isIgnoring}>
                {isIgnoring ? "Ignoring updates..." : "Ignore all updates"}
              </IgnoreButton>
              <ApplyButton
                type="button"
                onClick={() =>
                  onApply(
                    listingUpdates.updates.filter((update) =>
                      selectedFields.includes(update.field),
                    ),
                  )
                }
                disabled={selectedFields.length === 0 || isApplying}
              >
                {isApplying ? "Applying updates..." : "Apply selected updates"}
              </ApplyButton>
            </Actions>
          </Header>
          <Summary>
            {listingUpdates.updates.length} updates were received from MIQ for
            this listing.{" "}
            <strong>
              Selected updates will be applied to the listing and can be
              published later.
            </strong>
          </Summary>
          <HelpText>
            Updates that are not selected will be saved as discarded until MIQ
            provides a different value.
          </HelpText>
          <TableScroll>
            <UpdatesTable>
              <thead>
                <tr>
                  <CheckboxHeader>
                    <Checkbox
                      type="checkbox"
                      aria-label="Select all MIQ updates"
                      checked={allSelected}
                      onChange={() =>
                        setSelectedFields(allSelected ? [] : allFields)
                      }
                    />
                  </CheckboxHeader>
                  <th>Field</th>
                  <th>Current Listing Value</th>
                  <ArrowHeader aria-hidden="true" />
                  <th>MIQ Value</th>
                </tr>
              </thead>
              <tbody>
                <MiqUpdateSection
                  status="new"
                  updates={listingUpdates.updates.filter(
                    (update) => update.status === "new",
                  )}
                  selectedFields={selectedFields}
                  onToggle={toggleField}
                />
                <MiqUpdateSection
                  status="discarded"
                  updates={listingUpdates.updates.filter(
                    (update) => update.status === "discarded",
                  )}
                  selectedFields={selectedFields}
                  onToggle={toggleField}
                />
              </tbody>
            </UpdatesTable>
          </TableScroll>
        </ReviewContent>
      </ReviewPanel>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000000000;
  background: rgba(0, 0, 0, 0.28);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
`;

const ReviewPanel = styled.div`
  width: 100%;
  min-height: 294px;
  max-height: 100vh;
  overflow: auto;
  background: #fff;
  padding: 35px 24px 44px;
  box-sizing: border-box;
  color: #404040;
  font-family: ${(props) =>
    props.theme && props.theme.font
      ? props.theme.font.primary
      : "helvetica, arial, sans-serif"};

  @media (max-width: 900px) {
    padding: 30px 20px 40px;
  }

  @media (max-width: 640px) {
    padding: 24px 14px 32px;
  }

  @media (max-width: 420px) {
    padding: 20px 10px 28px;
  }
`;

const ReviewContent = styled.div`
  width: 600px;
  max-width: calc(100vw - 48px);
  margin: 0 auto;

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: 40px;
  padding-bottom: 8px;
  border-bottom: 1px solid #9e9e9e;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const Title = styled.h2`
  margin: 0;
  color: #565656;
  font-family: ${(props) =>
    props.theme && props.theme.font
      ? props.theme.font.bold
      : "helvetica, arial, sans-serif"};
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const IgnoreButton = styled.button`
  border: 0;
  background: transparent;
  color: #777777;
  cursor: pointer;
  font-family: inherit;
  font-size: 9px;
  white-space: nowrap;
  text-transform: uppercase;

  &:focus-visible {
    outline: 2px solid #006a4d;
    outline-offset: 2px;
  }
`;

const ApplyButton = styled.button`
  min-width: 140px;
  height: 30px;
  padding: 0 16px;
  border: 0;
  border-radius: 16px;
  background: #00a657;
  color: #fff;
  cursor: pointer;
  font-family: ${(props) =>
    props.theme && props.theme.font
      ? props.theme.font.bold
      : "helvetica, arial, sans-serif"};
  font-size: 9px;
  line-height: 30px;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 420px) {
    min-width: 0;
    width: 100%;
  }

  &:disabled {
    background: #b7b7b7;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #006a4d;
    outline-offset: 2px;
  }
`;

const Summary = styled.p`
  margin: 24px 0 12px;
  color: #555;
  font-size: 11px;
  line-height: 1.45;

  @media (max-width: 420px) {
    br {
      display: none;
    }
  }
`;

const HelpText = styled.p`
  margin: 0 0 24px;
  color: #686868;
  font-size: 10px;
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const UpdatesTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  color: #555;
  font-size: 11px;

  th {
    padding: 6px 7px;
    border-bottom: 1px solid #dedede;
    color: #555;
    font-family: ${(props) =>
      props.theme && props.theme.font
        ? props.theme.font.bold
        : "helvetica, arial, sans-serif"};
    text-align: left;
    text-transform: uppercase;
    font-size: 10px;
  }

  td {
    height: 20px;
    padding: 5px 7px;
    box-sizing: border-box;
    vertical-align: middle;
    overflow-wrap: anywhere;
    word-break: normal;
  }

  th:nth-child(1),
  td:nth-child(1) {
    padding-left: 0;
    padding-right: 8px;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 24%;
    padding-left: 0;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 31%;
  }

  th:nth-child(5),
  td:nth-child(5) {
    width: auto;
  }

  @media (max-width: 640px) {
    font-size: 10px;

    th {
      padding: 6px 4px;
      font-size: 9px;
      overflow-wrap: anywhere;
    }

    td {
      padding: 4px;
    }

    th:nth-child(2),
    td:nth-child(2) {
      width: 25%;
    }

    th:nth-child(3),
    td:nth-child(3) {
      width: 28%;
    }
  }

  @media (max-width: 420px) {
    font-size: 9px;

    th:nth-child(2),
    td:nth-child(2) {
      width: 28%;
    }

    th:nth-child(3),
    td:nth-child(3) {
      width: 25%;
    }
  }
`;

const CheckboxHeader = styled.th`
  width: 28px;

  @media (max-width: 420px) {
    width: 24px;
  }
`;

const ArrowHeader = styled.th`
  width: 26px;

  @media (max-width: 420px) {
    width: 18px;
  }
`;

export default MiqSyncReview;
